import {isDensityUnit, isMolarityUnit, isVolumeUnit} from 'inventory/InventoryUnitUtil';
import {LotProperties} from 'notebook/integrations/chemistry/custom-evals/LotPropertiesCustomEval';
import {resolveUnitsFromTypedValues} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryUnits';
import {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  CustomResolutionQueryType,
  StringResolutionType,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  coerceToPrimitiveNumber,
  isTypedValueEmpty,
  isUnitReferenceTypedValue,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {UnitReferenceTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';
import {applySigFigs, countSigFigs} from 'util/precise-decimal/SignificantFigures';

const PURITY_RANGE_ERROR = 'Purity must be a number between 0 and 100%.';
const DEFAULT_PURITY = '100';

/** Relative tolerance for floating-point comparison (1e-9). */
function isApproximatelyEqualRelative(a: number, b: number): boolean {
  if (a === b) {
    return true;
  }
  const diff = Math.abs(a - b);
  const largest = Math.max(Math.abs(a), Math.abs(b));
  return diff <= largest * 1e-9;
}

const AMOUNT_UNIT_MASS_ERROR =
  'Lot has a defined concentration. Amount unit must be a volume unit (e.g., mL, L).';

const UNIT_TYPE_MISMATCH_MOLAR =
  'Density units are not supported for this lot because concentration is defined in molarity.';
const UNIT_TYPE_MISMATCH_DENSITY =
  'Molarity units are not supported for this lot because concentration is defined in density.';

/**
 * Checks if the amount unit (cellDependencies[0]) is a mass unit.
 * If so, returns a ValueError. If volume or not yet resolved, returns null.
 */
function getAmountUnitMassError(
  cellDependencies: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): ExcessUtil.ValueError | null {
  const amountUnitTypedValue = cellDependencies[0] ?? null;
  if (!isUnitReferenceTypedValue(amountUnitTypedValue)) {
    // No amount unit selected yet — no error
    return null;
  }

  const {units, error} = resolveUnitsFromTypedValues([amountUnitTypedValue], resolveFromResolutionQuery);
  if (error != null || units == null || units.length === 0) {
    // Units still loading — don't error yet
    return null;
  }

  const amountUnit = units.find((u) => u.id === amountUnitTypedValue.unitSummary.id);
  if (amountUnit == null || isVolumeUnit(amountUnit)) {
    return null;
  }

  // Amount unit is mass (not volume) — incompatible with lot concentration
  return new ExcessUtil.ValueError(AMOUNT_UNIT_MASS_ERROR);
}

/**
 * Checks if the current concentration unit is compatible with the lot's concentration type.
 * Molar lots only support molarity units; density lots only support density units.
 * Returns null if compatible, or a ValueError if incompatible.
 */
function getUnitTypeMismatchError(
  lotConcentrationType: ConcentrationType,
  cellDependencies: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): ExcessUtil.ValueError | null {
  // The concentration unit is at index [2] for value column, but for the unit column
  // we check the current typedValue directly. This helper checks cellDependencies[2].
  const concUnitTypedValue = cellDependencies[2] ?? null;
  if (!isUnitReferenceTypedValue(concUnitTypedValue)) {
    return null; // No unit selected yet
  }

  const {units, error} = resolveUnitsFromTypedValues([concUnitTypedValue], resolveFromResolutionQuery);
  if (error != null || units == null || units.length === 0) {
    return null; // Still loading
  }

  const concUnit = units.find((u) => u.id === concUnitTypedValue.unitSummary.id);
  if (concUnit == null) {
    return null;
  }

  if (lotConcentrationType === 'molar' && isDensityUnit(concUnit)) {
    return new ExcessUtil.ValueError(UNIT_TYPE_MISMATCH_MOLAR);
  }
  if (lotConcentrationType === 'density' && isMolarityUnit(concUnit)) {
    return new ExcessUtil.ValueError(UNIT_TYPE_MISMATCH_DENSITY);
  }

  return null;
}

/**
 * Same as getUnitTypeMismatchError but checks a resolved MeasurementUnit directly
 * (used by the unit column eval which has the unit as its own typedValue).
 */
function getUnitTypeMismatchErrorForUnit(
  lotConcentrationType: ConcentrationType,
  unitTypedValue: TypedValue,
  resolveFromResolutionQuery: ResolveQueryFn
): ExcessUtil.ValueError | null {
  if (!isUnitReferenceTypedValue(unitTypedValue)) {
    return null;
  }

  const {units, error} = resolveUnitsFromTypedValues([unitTypedValue], resolveFromResolutionQuery);
  if (error != null || units == null || units.length === 0) {
    return null;
  }

  const concUnit = units.find((u) => u.id === unitTypedValue.unitSummary.id);
  if (concUnit == null) {
    return null;
  }

  if (lotConcentrationType === 'molar' && isDensityUnit(concUnit)) {
    return new ExcessUtil.ValueError(UNIT_TYPE_MISMATCH_MOLAR);
  }
  if (lotConcentrationType === 'density' && isMolarityUnit(concUnit)) {
    return new ExcessUtil.ValueError(UNIT_TYPE_MISMATCH_DENSITY);
  }

  return null;
}

/**
 * Resolves lot properties from the shared resolution cache.
 * Returns {properties, loading} — loading is true while the resolution is in-flight.
 */
function resolveLotProperties(
  lotCellValue: TypedValue | null,
  resolveFromResolutionQuery: ResolveQueryFn
): {properties: LotProperties | null; loading: boolean} {
  if (lotCellValue == null || lotCellValue.type !== BaseTypedValueType.BIOENTITY) {
    return {properties: null, loading: false};
  }

  const lotId = lotCellValue.bioentitySummary.id;
  const {resolvedValue, error: resolutionError} = resolveFromResolutionQuery(
    lotCellValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_LOT_PROPERTIES,
      lotId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );

  // If resolution errored, check whether it's still loading or a real error.
  // Real errors (e.g. "not a molecule lot") mean no properties are available.
  if (resolutionError != null) {
    const isLoading = (resolutionError as {type?: string}).type === ExcessUtil.LoadingError.type;
    return {properties: null, loading: isLoading};
  }

  if (resolvedValue == null || resolvedValue.type !== BaseTypedValueType.JSON) {
    return {properties: null, loading: false};
  }

  return {properties: resolvedValue.jsonValue as LotProperties, loading: false};
}

type ConcentrationType = 'molar' | 'density';

/**
 * Gets the lot's concentration value and unit symbol.
 * Returns null if either value or unit is missing (both are required for auto-fill).
 * Prefers molar concentration; falls back to density.
 */
function getLotConcentration(
  lotProperties: LotProperties
): {value: number; unitId: string; unitSymbol: string; concentrationType: ConcentrationType} | null {
  if (
    lotProperties.concentrationMolar != null &&
    lotProperties.concentrationMolarUnitId != null &&
    lotProperties.concentrationMolarUnitSymbol != null
  ) {
    return {
      value: lotProperties.concentrationMolar,
      unitId: lotProperties.concentrationMolarUnitId,
      unitSymbol: lotProperties.concentrationMolarUnitSymbol,
      concentrationType: 'molar',
    };
  }
  if (
    lotProperties.concentrationDensity != null &&
    lotProperties.concentrationDensityUnitId != null &&
    lotProperties.concentrationDensityUnitSymbol != null
  ) {
    return {
      value: lotProperties.concentrationDensity,
      unitId: lotProperties.concentrationDensityUnitId,
      unitSymbol: lotProperties.concentrationDensityUnitSymbol,
      concentrationType: 'density',
    };
  }
  return null;
}

/**
 * Custom eval for the purity column that auto-fills from the lot's purity value.
 *
 * cellDependencies[0] = lot column's evaluated value (BIOENTITY)
 *
 * Behavior:
 * - Lot selected + has purity + cell empty → auto-fill with lot's purity
 * - No lot (or lot has no purity) + cell empty → default to 100%
 * - Cell has value → validate 0-100% range
 * - Lot loading → loading error
 */
export function getLotPurityCustomEval(
  typedValue: TypedValue,
  _columnDefinition: ColumnDefinition | null,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: StoichiometryCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  const lotCellValue = cellDependencies[0];
  const {properties: lotProperties, loading} = resolveLotProperties(lotCellValue, resolveFromResolutionQuery);

  // If lot is selected but still loading, show loading error
  if (loading) {
    return {val: typedValue, error: new ExcessUtil.LoadingError(''), locked: false};
  }

  // If the cell is empty, auto-fill from lot or use default
  if (isTypedValueEmpty(typedValue)) {
    const hasLotPurity = lotProperties?.purity != null;
    const purityValue = hasLotPurity ? String(lotProperties.purity) : DEFAULT_PURITY;
    const autoFilledValue = {
      type: BaseTypedValueType.STRING,
      rawValue: purityValue,
    } as TypedValue;
    // Lock the cell when auto-filled from lot (user must clear lot to edit)
    return {val: autoFilledValue, error: null, locked: hasLotPurity};
  }

  // Validate the value (manually entered)
  const numericValue = coerceToPrimitiveNumber(typedValue);
  if (numericValue == null || numericValue <= 0 || numericValue > 100) {
    return {val: typedValue, error: new ExcessUtil.ValueError(PURITY_RANGE_ERROR), locked: false};
  }

  // If lot has purity and it doesn't match the manual value, warn
  if (lotProperties?.purity != null && numericValue !== lotProperties.purity) {
    return {
      val: typedValue,
      error: new ExcessUtil.ValueError(`Purity does not match lot value (${lotProperties.purity}%).`),
      locked: false,
    };
  }

  return {val: typedValue, error: null, locked: false};
}

/**
 * Custom eval for the concentration VALUE column that auto-fills from the lot.
 *
 * cellDependencies layout (from getCustomEvalDependencies):
 *   [0] = CHEMISTRY_AMOUNT_UNIT
 *   [1] = CHEMISTRY_LOT (lot BIOENTITY)
 *   [2] = CHEMISTRY_CONCENTRATION_UNIT (current unit in the table)
 *
 * Behavior:
 * - Cell empty + lot has concentration (value + unit) → auto-fill value (scaled to current unit)
 * - Cell has value + lot has concentration → compare raw numbers, warn if different
 * - No lot or lot has no valid concentration → don't auto-fill (fall through)
 * - Lot loading → loading error
 */
export function getLotConcentrationValue(
  typedValue: TypedValue,
  cellDependencies: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): {autoFilled: boolean; result: CustomEvalResult} {
  const lotCellValue = cellDependencies[1] ?? null;
  const {properties: lotProperties, loading} = resolveLotProperties(lotCellValue, resolveFromResolutionQuery);

  // If lot is selected but still loading, show loading error
  if (loading) {
    return {
      autoFilled: true,
      result: {val: typedValue, error: new ExcessUtil.LoadingError(''), locked: false},
    };
  }

  // No lot or lot has no valid concentration → fall through to existing logic
  if (lotProperties == null) {
    return {autoFilled: false, result: {val: typedValue, error: null, locked: false}};
  }

  const lotConcentration = getLotConcentration(lotProperties);
  if (lotConcentration == null) {
    return {autoFilled: false, result: {val: typedValue, error: null, locked: false}};
  }

  // Lot has concentration — check if amount unit is mass (incompatible)
  const amountUnitError = getAmountUnitMassError(cellDependencies, resolveFromResolutionQuery);
  if (amountUnitError != null) {
    return {autoFilled: true, result: {val: typedValue, error: amountUnitError, locked: false}};
  }

  // Check unit type compatibility (molar lot → only molarity units; density lot → only density units)
  const unitTypeMismatchError = getUnitTypeMismatchError(
    lotConcentration.concentrationType,
    cellDependencies,
    resolveFromResolutionQuery
  );
  if (unitTypeMismatchError != null) {
    return {autoFilled: true, result: {val: typedValue, error: unitTypeMismatchError, locked: false}};
  }

  // Compute the scaled concentration value based on the current unit column
  const scaledValue = getScaledConcentrationValue(
    lotConcentration,
    cellDependencies,
    resolveFromResolutionQuery
  );

  // If units are still loading, show loading error
  if (scaledValue == null) {
    return {
      autoFilled: true,
      result: {val: typedValue, error: new ExcessUtil.LoadingError(''), locked: false},
    };
  }

  // Cell is empty → auto-fill with scaled lot concentration value
  if (isTypedValueEmpty(typedValue)) {
    const autoFilledValue = {
      type: BaseTypedValueType.STRING,
      rawValue: String(scaledValue),
    } as TypedValue;
    return {autoFilled: true, result: {val: autoFilledValue, error: null, locked: true}};
  }

  // Cell has a value → compare with scaled lot value (use relative tolerance for floating point)
  const cellNumericValue = coerceToPrimitiveNumber(typedValue);
  if (cellNumericValue != null && !isApproximatelyEqualRelative(cellNumericValue, scaledValue)) {
    return {
      autoFilled: true,
      result: {
        val: typedValue,
        error: new ExcessUtil.ValueError(
          `Concentration does not match lot value (${lotConcentration.value} ${lotConcentration.unitSymbol}).`
        ),
        locked: false,
      },
    };
  }

  // Cell value matches lot → no warning needed
  return {autoFilled: true, result: {val: typedValue, error: null, locked: false}};
}

/**
 * Scales the lot's concentration value to the current unit displayed in the table.
 * Returns null if units are still loading.
 * If no current unit or same unit, returns the raw lot value (no scaling needed).
 */
function getScaledConcentrationValue(
  lotConcentration: {value: number; unitId: string; unitSymbol: string},
  cellDependencies: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): number | null {
  const currentUnitTypedValue = cellDependencies[2] ?? null;

  // If no unit is set in the table yet, return raw value (we'll also auto-fill the unit)
  if (!isUnitReferenceTypedValue(currentUnitTypedValue)) {
    return lotConcentration.value;
  }

  const currentUnitId = currentUnitTypedValue.unitSummary.id;

  // Same unit → no scaling needed
  if (currentUnitId === lotConcentration.unitId) {
    return lotConcentration.value;
  }

  // Different unit → resolve both to get conversion factors
  const {units, error} = resolveUnitsFromTypedValues(
    [
      currentUnitTypedValue,
      {type: BaseTypedValueType.UNIT_REFERENCE, unitSummary: {id: lotConcentration.unitId}} as TypedValue,
    ],
    resolveFromResolutionQuery
  );

  if (error != null || units == null || units.length < 2) {
    return null; // Still loading
  }

  const currentUnit = units.find((u) => u.id === currentUnitId);
  const lotUnit = units.find((u) => u.id === lotConcentration.unitId);

  if (currentUnit == null || lotUnit == null) {
    return lotConcentration.value; // Fallback: no scaling
  }

  // Scale: lotValue * (lotUnit.conversionFactor / currentUnit.conversionFactor)
  // Use PreciseDecimal to avoid floating point artifacts, then preserve
  // the significant figures from the lot's original value.
  const lotValue = PreciseDecimal.of(lotConcentration.value);
  const lotFactor = PreciseDecimal.of(lotUnit.conversionFactor);
  const currentFactor = PreciseDecimal.of(currentUnit.conversionFactor);
  if (currentFactor.decimal.isZero()) {
    return lotConcentration.value;
  }

  const scaled = PreciseDecimal.of(lotValue.decimal.mul(lotFactor.decimal).div(currentFactor.decimal));
  const sigFigs = countSigFigs(lotValue);
  const rounded = applySigFigs(scaled, sigFigs);
  return rounded.toFloat();
}

/**
 * Custom eval for the concentration UNIT column that auto-fills from the lot.
 *
 * cellDependencies layout (from getCustomEvalDependencies):
 *   [0] = CHEMISTRY_AMOUNT_UNIT
 *   [1] = CHEMISTRY_LOT (lot BIOENTITY)
 *
 * Behavior:
 * - Lot has concentration with unit → resolve and set unit (only when cell is empty)
 * - No lot or lot has no valid concentration → don't auto-fill (fall through)
 * - Lot loading or unit resolution pending → loading error
 */
export function getLotConcentrationUnitValue(
  typedValue: TypedValue,
  cellDependencies: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): {autoFilled: boolean; result: CustomEvalResult} {
  const lotCellValue = cellDependencies[1] ?? null;
  const {properties: lotProperties, loading: lotLoading} = resolveLotProperties(
    lotCellValue,
    resolveFromResolutionQuery
  );

  // If lot is selected but still loading properties, show loading error
  if (lotLoading) {
    return {
      autoFilled: true,
      result: {val: typedValue, error: new ExcessUtil.LoadingError(''), locked: false},
    };
  }

  // No lot or no valid concentration → fall through
  if (lotProperties == null) {
    return {autoFilled: false, result: {val: typedValue, error: null, locked: false}};
  }

  const lotConcentration = getLotConcentration(lotProperties);
  if (lotConcentration == null) {
    return {autoFilled: false, result: {val: typedValue, error: null, locked: false}};
  }

  // Lot has concentration — check if amount unit is mass (incompatible)
  const amountUnitError = getAmountUnitMassError(cellDependencies, resolveFromResolutionQuery);
  if (amountUnitError != null) {
    return {autoFilled: true, result: {val: typedValue, error: amountUnitError, locked: false}};
  }

  // If the user has already selected a unit, don't overwrite it — but validate type compatibility
  if (!isTypedValueEmpty(typedValue)) {
    const unitTypeMismatchError = getUnitTypeMismatchErrorForUnit(
      lotConcentration.concentrationType,
      typedValue,
      resolveFromResolutionQuery
    );
    if (unitTypeMismatchError != null) {
      return {autoFilled: true, result: {val: typedValue, error: unitTypeMismatchError, locked: false}};
    }
    return {autoFilled: true, result: {val: typedValue, error: null, locked: false}};
  }

  // Resolve the lot's unit symbol to a UnitReferenceTypedValue
  const {resolvedValue, error: unitResolutionError} = resolveFromResolutionQuery(
    toStringTypedValue(lotConcentration.unitSymbol),
    {
      string: lotConcentration.unitSymbol,
      type: ResolutionQueryType.STRING,
      valueType: {
        type: StringResolutionType.UNIT_REFERENCE,
      },
    },
    (resolved: UnitReferenceTypedValue): UnitReferenceTypedValue => resolved
  );

  // Unit resolution still loading
  if (unitResolutionError != null) {
    return {autoFilled: true, result: {val: typedValue, error: unitResolutionError, locked: false}};
  }

  // Auto-fill the unit (not locked — user can change within unit type)
  return {autoFilled: true, result: {val: resolvedValue, error: null, locked: false}};
}
